<script lang="ts">
  import { provide, ref } from 'vue'
  import { createContext } from 'reka-ui'
  import { appLoadingKey } from '@gat-ship/types/injection-keys'
  import type { AppLoadingContext } from '@gat-ship/types/injection-keys'

  export type { AppLoadingContext }

  const [_useAppLoading, provideAppLoading] =
    createContext<AppLoadingContext>('gs-app-loading')

  export function useAppLoading() {
    try {
      return _useAppLoading()
    } catch {
      console.warn('AppLoadingProvider not provided')
      return {
        loadingIds: new Set<string>(),
        setLoadingId: () => {},
        removeLoadingId: () => {},
        isLoading: () => false
      }
    }
  }
</script>

<script setup lang="ts">
  const loadingIds = ref(new Set<string>())

  function setLoadingId(id?: string | string[]) {
    if (!id) {
      return
    }
    if (Array.isArray(id)) {
      id.forEach((id) => {
        loadingIds.value.add(id)
      })
    } else {
      loadingIds.value.add(id)
    }
  }

  function removeLoadingId(id?: string | string[]) {
    if (!id) {
      return
    }
    if (Array.isArray(id)) {
      id.forEach((id) => {
        loadingIds.value.delete(id)
      })
    } else {
      loadingIds.value.delete(id)
    }
  }

  function isLoading(id?: string | string[]) {
    if (!id) {
      return false
    }
    if (Array.isArray(id)) {
      return id.some((id) => loadingIds.value.has(id))
    }
    return loadingIds.value.has(id)
  }

  const appLoading = {
    loadingIds,
    setLoadingId,
    removeLoadingId,
    isLoading
  }

  provideAppLoading(appLoading)
  // Also expose via a UI-free injection key so non-UI layers can read
  // the root-provided handle without importing `useAppLoading()`.
  provide(appLoadingKey, appLoading)
</script>

<template>
  <slot />
</template>
